/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 03
 *   DB_十神 (Sheet45, A1:I101)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_十神 dataset status = AUTHORITATIVE_DATASET_COMPLETE
 *     - Section A (five-element auxiliary matrix): A2:E6 = 25 cells;
 *       A7:E101 are EMPTY in Excel (CountA=0) — no rows manufactured.
 *     - Section B (十神 lookup matrix): F2:I101 = exactly 100 rows,
 *       complete 10 × 10 day-stem × target-stem matrix, 100 unique F keys.
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL.
 *     (Batch 01 = DB_天干, DB_地支; Batch 02 = DB_藏干; Batch 03 = DB_十神.
 *     Remaining DB tables — Sheet39 partial, Sheet43, Sheet47, and any
 *     others required by later batches — NOT_STARTED.)
 *
 * ISOLATION
 *   Does NOT write into `src/engine/referenceData.js`. Production
 *   ReferenceData registry continues to hold only MOCK_POC placeholders.
 *   Does NOT wire into the main V2.9.9 pipeline.
 *   Does NOT modify M02 Batch 01/Batch 02 behaviour — no cross-import
 *   is made from those modules.
 *
 * UNICODE POLICY (MIGRATION_RULES.md → Encoding Warning)
 *   Every Chinese literal is constructed from its audited AscW/Hex
 *   code point via `String.fromCodePoint(...)`. No direct Chinese glyph
 *   is used inside code — a comment on each entry states the canonical
 *   glyph for reader convenience.
 *
 * HEADER DISCREPANCY RESOLUTION (documented explicitly)
 *   The Batch 03 audit fixture supplied header labels for columns B/C/D/E
 *   and H that did NOT match their AscW-derived Unicode code points.
 *   Per MIGRATION_RULES.md, the code-point evidence is authoritative and
 *   the transcribed label is discarded. Resolutions applied here:
 *
 *     Column | Transcribed label   | Unicode-derived (AUTHORITATIVE)
 *     -------+---------------------+---------------------------------
 *     B      | 同类(比劫)          | 我生(食伤)
 *     C      | 同生(印)            | 我克(财)
 *     D      | 生同(食)            | 生我(印)
 *     E      | 克同(财星)          | 克我(官杀)
 *     H      | 目标天干            | 对方天干
 *
 *   The A2:E6 row data itself is INTERNALLY CONSISTENT with the
 *   Unicode-authoritative headers (e.g. row 木 → B=火 matches "我生", not
 *   "同类" which would be 木). No numeric or string cell content is
 *   modified — only the header labels stored here are the AscW-derived
 *   forms.
 *
 * LOOKUP CONTRACT
 *   Exact-match Unicode-safe equality. Missing keys throw
 *   `MISSING_REFERENCE_KEY` — the M02 Batch 01 / Batch 02 convention
 *   used for AUTHORITATIVE_DATASET_COMPLETE datasets (distinct from
 *   M03 partial-audit slices which throw `MISSING_REFERENCE_EVIDENCE`).
 *   No fuzzy match, no transliteration, no alias substitution.
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Byte-wise identical output
 *   on every run. Freeze at dataset + rows + per-row level.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── 五行 (five elements) ────────────────────────────────────────────────────
// AscW-derived code points supplied in the Batch 03 audit fixture:
//   木 = 26408 (0x6728), 火 = 28779 (0x706B), 土 = 22303 (0x571F),
//   水 = 27700 (0x6C34), 金 = 37329 (0x91D1)
const EL_WOOD  = String.fromCodePoint(26408); // 木
const EL_FIRE  = String.fromCodePoint(28779); // 火
const EL_EARTH = String.fromCodePoint(22303); // 土
const EL_WATER = String.fromCodePoint(27700); // 水
const EL_METAL = String.fromCodePoint(37329); // 金

// ── 天干 (10 stems) ────────────────────────────────────────────────────────
// AscW-derived code points supplied in the Batch 03 audit fixture:
//   甲=30002 乙=20057 丙=19993 丁=19969 戊=25098
//   己=24049 庚=24218 辛=36763 壬=22764 癸=30328
const CH_JIA  = String.fromCodePoint(30002); // 甲
const CH_YI   = String.fromCodePoint(20057); // 乙
const CH_BING = String.fromCodePoint(19993); // 丙
const CH_DING = String.fromCodePoint(19969); // 丁
const CH_WU   = String.fromCodePoint(25098); // 戊
const CH_JI   = String.fromCodePoint(24049); // 己
const CH_GENG = String.fromCodePoint(24218); // 庚
const CH_XIN  = String.fromCodePoint(36763); // 辛
const CH_REN  = String.fromCodePoint(22764); // 壬
const CH_GUI  = String.fromCodePoint(30328); // 癸

// ── 十神 tokens (10 audited types) ─────────────────────────────────────────
// AscW-derived code points from Batch 03 audit fixture:
//   比肩 = [27604,32937]   劫财 = [21163,36130]   食神 = [39135,31070]
//   伤官 = [20260,23448]   偏财 = [20559,36130]   正财 = [27491,36130]
//   七杀 = [19971,26432]   正官 = [27491,23448]   偏印 = [20559,21360]
//   正印 = [27491,21360]
export const SHI_SHEN_TOKEN = Object.freeze({
  BI_JIAN:   String.fromCodePoint(27604, 32937), // 比肩
  JIE_CAI:   String.fromCodePoint(21163, 36130), // 劫财
  SHI_SHEN:  String.fromCodePoint(39135, 31070), // 食神
  SHANG_GUAN:String.fromCodePoint(20260, 23448), // 伤官
  PIAN_CAI:  String.fromCodePoint(20559, 36130), // 偏财
  ZHENG_CAI: String.fromCodePoint(27491, 36130), // 正财
  QI_SHA:    String.fromCodePoint(19971, 26432), // 七杀
  ZHENG_GUAN:String.fromCodePoint(27491, 23448), // 正官
  PIAN_YIN:  String.fromCodePoint(20559, 21360), // 偏印
  ZHENG_YIN: String.fromCodePoint(27491, 21360), // 正印
});

// Composite-key separator (audited ASCII underscore U+005F).
const KEY_SEP = "_";

/** Compose the audited composite key `<日主>_<对方>`. */
function ck(dayStem, targetStem) {
  return dayStem + KEY_SEP + targetStem;
}

// ── Headers (Unicode-authoritative — see HEADER DISCREPANCY RESOLUTION) ────
// Column A: 五行           [20116, 34892]
// Column B: 我生(食伤)     [25105, 29983, 0x28, 39135, 20260, 0x29]
// Column C: 我克(财)       [25105, 20811, 0x28, 36130, 0x29]
// Column D: 生我(印)       [29983, 25105, 0x28, 21360, 0x29]
// Column E: 克我(官杀)     [20811, 25105, 0x28, 23448, 26432, 0x29]
// Column F: 复合键         [22797, 21512, 38190]
// Column G: 日主天干       [26085, 20027, 22825, 24178]
// Column H: 对方天干       [23545, 26041, 22825, 24178]
// Column I: 十神           [21313, 31070]
const HEADER_WU_XING           = String.fromCodePoint(20116, 34892);
const HEADER_WO_SHENG_SHI_SHANG= String.fromCodePoint(25105, 29983, 0x28, 39135, 20260, 0x29);
const HEADER_WO_KE_CAI         = String.fromCodePoint(25105, 20811, 0x28, 36130, 0x29);
const HEADER_SHENG_WO_YIN      = String.fromCodePoint(29983, 25105, 0x28, 21360, 0x29);
const HEADER_KE_WO_GUAN_SHA    = String.fromCodePoint(20811, 25105, 0x28, 23448, 26432, 0x29);
const HEADER_FU_HE_JIAN        = String.fromCodePoint(22797, 21512, 38190);
const HEADER_RI_ZHU_TIAN_GAN   = String.fromCodePoint(26085, 20027, 22825, 24178);
const HEADER_DUI_FANG_TIAN_GAN = String.fromCodePoint(23545, 26041, 22825, 24178);
const HEADER_SHI_SHEN          = String.fromCodePoint(21313, 31070);

// ── Section A: 5×5 five-element auxiliary matrix (Excel A2:E6) ─────────────
// Row keyed by column-A element; columns B/C/D/E give the target element
// under each relationship. A7:E101 EMPTY in Excel (CountA=0).
const FIVE_ELEMENT_AUX_ROWS = Object.freeze([
  Object.freeze({ wuXing: EL_WOOD,  woShengShiShang: EL_FIRE,  woKeCai: EL_EARTH, shengWoYin: EL_WATER, keWoGuanSha: EL_METAL }), // 木 火 土 水 金
  Object.freeze({ wuXing: EL_FIRE,  woShengShiShang: EL_EARTH, woKeCai: EL_METAL, shengWoYin: EL_WOOD,  keWoGuanSha: EL_WATER }), // 火 土 金 木 水
  Object.freeze({ wuXing: EL_EARTH, woShengShiShang: EL_METAL, woKeCai: EL_WATER, shengWoYin: EL_FIRE,  keWoGuanSha: EL_WOOD  }), // 土 金 水 火 木
  Object.freeze({ wuXing: EL_METAL, woShengShiShang: EL_WATER, woKeCai: EL_WOOD,  shengWoYin: EL_EARTH, keWoGuanSha: EL_FIRE  }), // 金 水 木 土 火
  Object.freeze({ wuXing: EL_WATER, woShengShiShang: EL_WOOD,  woKeCai: EL_FIRE,  shengWoYin: EL_METAL, keWoGuanSha: EL_EARTH }), // 水 木 火 金 土
]);

// ── Section B: 100-row 十神 lookup matrix (Excel F2:I101) ──────────────────
// Ordered as in the Excel Golden Master: for each 日主 in canonical stem
// order (甲乙丙丁戊己庚辛壬癸), the 10 对方 rows appear in the same order.
const SS = SHI_SHEN_TOKEN;
const SHI_SHEN_ROWS = Object.freeze([
  // 甲 (10)
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_JIA ), riZhu: CH_JIA,  duiFang: CH_JIA,  shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_YI  ), riZhu: CH_JIA,  duiFang: CH_YI,   shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_BING), riZhu: CH_JIA,  duiFang: CH_BING, shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_DING), riZhu: CH_JIA,  duiFang: CH_DING, shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_WU  ), riZhu: CH_JIA,  duiFang: CH_WU,   shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_JI  ), riZhu: CH_JIA,  duiFang: CH_JI,   shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_GENG), riZhu: CH_JIA,  duiFang: CH_GENG, shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_XIN ), riZhu: CH_JIA,  duiFang: CH_XIN,  shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_REN ), riZhu: CH_JIA,  duiFang: CH_REN,  shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_JIA,  CH_GUI ), riZhu: CH_JIA,  duiFang: CH_GUI,  shiShen: SS.ZHENG_YIN  }),
  // 乙 (10)
  Object.freeze({ compositeKey: ck(CH_YI,   CH_JIA ), riZhu: CH_YI,   duiFang: CH_JIA,  shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_YI  ), riZhu: CH_YI,   duiFang: CH_YI,   shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_BING), riZhu: CH_YI,   duiFang: CH_BING, shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_DING), riZhu: CH_YI,   duiFang: CH_DING, shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_WU  ), riZhu: CH_YI,   duiFang: CH_WU,   shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_JI  ), riZhu: CH_YI,   duiFang: CH_JI,   shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_GENG), riZhu: CH_YI,   duiFang: CH_GENG, shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_XIN ), riZhu: CH_YI,   duiFang: CH_XIN,  shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_REN ), riZhu: CH_YI,   duiFang: CH_REN,  shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_YI,   CH_GUI ), riZhu: CH_YI,   duiFang: CH_GUI,  shiShen: SS.PIAN_YIN   }),
  // 丙 (10)
  Object.freeze({ compositeKey: ck(CH_BING, CH_JIA ), riZhu: CH_BING, duiFang: CH_JIA,  shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_YI  ), riZhu: CH_BING, duiFang: CH_YI,   shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_BING), riZhu: CH_BING, duiFang: CH_BING, shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_DING), riZhu: CH_BING, duiFang: CH_DING, shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_WU  ), riZhu: CH_BING, duiFang: CH_WU,   shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_JI  ), riZhu: CH_BING, duiFang: CH_JI,   shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_GENG), riZhu: CH_BING, duiFang: CH_GENG, shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_XIN ), riZhu: CH_BING, duiFang: CH_XIN,  shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_REN ), riZhu: CH_BING, duiFang: CH_REN,  shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_BING, CH_GUI ), riZhu: CH_BING, duiFang: CH_GUI,  shiShen: SS.ZHENG_GUAN }),
  // 丁 (10)
  Object.freeze({ compositeKey: ck(CH_DING, CH_JIA ), riZhu: CH_DING, duiFang: CH_JIA,  shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_YI  ), riZhu: CH_DING, duiFang: CH_YI,   shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_BING), riZhu: CH_DING, duiFang: CH_BING, shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_DING), riZhu: CH_DING, duiFang: CH_DING, shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_WU  ), riZhu: CH_DING, duiFang: CH_WU,   shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_JI  ), riZhu: CH_DING, duiFang: CH_JI,   shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_GENG), riZhu: CH_DING, duiFang: CH_GENG, shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_XIN ), riZhu: CH_DING, duiFang: CH_XIN,  shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_REN ), riZhu: CH_DING, duiFang: CH_REN,  shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_DING, CH_GUI ), riZhu: CH_DING, duiFang: CH_GUI,  shiShen: SS.QI_SHA     }),
  // 戊 (10)
  Object.freeze({ compositeKey: ck(CH_WU,   CH_JIA ), riZhu: CH_WU,   duiFang: CH_JIA,  shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_YI  ), riZhu: CH_WU,   duiFang: CH_YI,   shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_BING), riZhu: CH_WU,   duiFang: CH_BING, shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_DING), riZhu: CH_WU,   duiFang: CH_DING, shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_WU  ), riZhu: CH_WU,   duiFang: CH_WU,   shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_JI  ), riZhu: CH_WU,   duiFang: CH_JI,   shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_GENG), riZhu: CH_WU,   duiFang: CH_GENG, shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_XIN ), riZhu: CH_WU,   duiFang: CH_XIN,  shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_REN ), riZhu: CH_WU,   duiFang: CH_REN,  shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_WU,   CH_GUI ), riZhu: CH_WU,   duiFang: CH_GUI,  shiShen: SS.ZHENG_CAI  }),
  // 己 (10)
  Object.freeze({ compositeKey: ck(CH_JI,   CH_JIA ), riZhu: CH_JI,   duiFang: CH_JIA,  shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_YI  ), riZhu: CH_JI,   duiFang: CH_YI,   shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_BING), riZhu: CH_JI,   duiFang: CH_BING, shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_DING), riZhu: CH_JI,   duiFang: CH_DING, shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_WU  ), riZhu: CH_JI,   duiFang: CH_WU,   shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_JI  ), riZhu: CH_JI,   duiFang: CH_JI,   shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_GENG), riZhu: CH_JI,   duiFang: CH_GENG, shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_XIN ), riZhu: CH_JI,   duiFang: CH_XIN,  shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_REN ), riZhu: CH_JI,   duiFang: CH_REN,  shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_JI,   CH_GUI ), riZhu: CH_JI,   duiFang: CH_GUI,  shiShen: SS.PIAN_CAI   }),
  // 庚 (10)
  Object.freeze({ compositeKey: ck(CH_GENG, CH_JIA ), riZhu: CH_GENG, duiFang: CH_JIA,  shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_YI  ), riZhu: CH_GENG, duiFang: CH_YI,   shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_BING), riZhu: CH_GENG, duiFang: CH_BING, shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_DING), riZhu: CH_GENG, duiFang: CH_DING, shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_WU  ), riZhu: CH_GENG, duiFang: CH_WU,   shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_JI  ), riZhu: CH_GENG, duiFang: CH_JI,   shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_GENG), riZhu: CH_GENG, duiFang: CH_GENG, shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_XIN ), riZhu: CH_GENG, duiFang: CH_XIN,  shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_REN ), riZhu: CH_GENG, duiFang: CH_REN,  shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_GENG, CH_GUI ), riZhu: CH_GENG, duiFang: CH_GUI,  shiShen: SS.SHANG_GUAN }),
  // 辛 (10)
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_JIA ), riZhu: CH_XIN,  duiFang: CH_JIA,  shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_YI  ), riZhu: CH_XIN,  duiFang: CH_YI,   shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_BING), riZhu: CH_XIN,  duiFang: CH_BING, shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_DING), riZhu: CH_XIN,  duiFang: CH_DING, shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_WU  ), riZhu: CH_XIN,  duiFang: CH_WU,   shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_JI  ), riZhu: CH_XIN,  duiFang: CH_JI,   shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_GENG), riZhu: CH_XIN,  duiFang: CH_GENG, shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_XIN ), riZhu: CH_XIN,  duiFang: CH_XIN,  shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_REN ), riZhu: CH_XIN,  duiFang: CH_REN,  shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_XIN,  CH_GUI ), riZhu: CH_XIN,  duiFang: CH_GUI,  shiShen: SS.SHI_SHEN   }),
  // 壬 (10)
  Object.freeze({ compositeKey: ck(CH_REN,  CH_JIA ), riZhu: CH_REN,  duiFang: CH_JIA,  shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_YI  ), riZhu: CH_REN,  duiFang: CH_YI,   shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_BING), riZhu: CH_REN,  duiFang: CH_BING, shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_DING), riZhu: CH_REN,  duiFang: CH_DING, shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_WU  ), riZhu: CH_REN,  duiFang: CH_WU,   shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_JI  ), riZhu: CH_REN,  duiFang: CH_JI,   shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_GENG), riZhu: CH_REN,  duiFang: CH_GENG, shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_XIN ), riZhu: CH_REN,  duiFang: CH_XIN,  shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_REN ), riZhu: CH_REN,  duiFang: CH_REN,  shiShen: SS.BI_JIAN    }),
  Object.freeze({ compositeKey: ck(CH_REN,  CH_GUI ), riZhu: CH_REN,  duiFang: CH_GUI,  shiShen: SS.JIE_CAI    }),
  // 癸 (10)
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_JIA ), riZhu: CH_GUI,  duiFang: CH_JIA,  shiShen: SS.SHANG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_YI  ), riZhu: CH_GUI,  duiFang: CH_YI,   shiShen: SS.SHI_SHEN   }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_BING), riZhu: CH_GUI,  duiFang: CH_BING, shiShen: SS.ZHENG_CAI  }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_DING), riZhu: CH_GUI,  duiFang: CH_DING, shiShen: SS.PIAN_CAI   }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_WU  ), riZhu: CH_GUI,  duiFang: CH_WU,   shiShen: SS.ZHENG_GUAN }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_JI  ), riZhu: CH_GUI,  duiFang: CH_JI,   shiShen: SS.QI_SHA     }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_GENG), riZhu: CH_GUI,  duiFang: CH_GENG, shiShen: SS.ZHENG_YIN  }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_XIN ), riZhu: CH_GUI,  duiFang: CH_XIN,  shiShen: SS.PIAN_YIN   }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_REN ), riZhu: CH_GUI,  duiFang: CH_REN,  shiShen: SS.JIE_CAI    }),
  Object.freeze({ compositeKey: ck(CH_GUI,  CH_GUI ), riZhu: CH_GUI,  duiFang: CH_GUI,  shiShen: SS.BI_JIAN    }),
]);

// ── Dataset export ─────────────────────────────────────────────────────────

/**
 * DB_十神 — 25-cell five-element auxiliary block + 100-row 十神 lookup
 * matrix, frozen at every level. Header labels stored as the AscW-derived
 * (Unicode-authoritative) forms; see HEADER DISCREPANCY RESOLUTION above.
 */
export const DB_SHI_SHEN = Object.freeze({
  codeName: "Sheet45",
  worksheetIndex: 45,
  sheetName: String.fromCodePoint(0x44, 0x42, 0x5F, 21313, 31070), // DB_十神
  sheetNameUnicode: [68, 66, 95, 21313, 31070],
  excelUsedRange: "A1:I101",
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master Value2 + AscW extraction",
  auditedInBatch: "M02_BATCH_03",
  keySeparator: KEY_SEP,
  headers: Object.freeze({
    A: HEADER_WU_XING,            // 五行
    B: HEADER_WO_SHENG_SHI_SHANG, // 我生(食伤)   [Unicode-authoritative]
    C: HEADER_WO_KE_CAI,          // 我克(财)     [Unicode-authoritative]
    D: HEADER_SHENG_WO_YIN,       // 生我(印)     [Unicode-authoritative]
    E: HEADER_KE_WO_GUAN_SHA,     // 克我(官杀)   [Unicode-authoritative]
    F: HEADER_FU_HE_JIAN,         // 复合键
    G: HEADER_RI_ZHU_TIAN_GAN,    // 日主天干
    H: HEADER_DUI_FANG_TIAN_GAN,  // 对方天干   [Unicode-authoritative]
    I: HEADER_SHI_SHEN,           // 十神
  }),
  fiveElementAux: FIVE_ELEMENT_AUX_ROWS, // A2:E6 — 5 rows × 5 cols = 25 cells
  fiveElementAuxRange: "A2:E6",
  fiveElementAuxRowCount: 5,
  emptyRangeAfterAux: "A7:E101", // Excel CountA = 0; no rows manufactured.
  shiShenRows: SHI_SHEN_ROWS,    // F2:I101 — exactly 100 rows.
  shiShenRange: "F2:I101",
  shiShenRowCount: 100,
  types: SHI_SHEN_TOKEN,
});

// ── Lookup API ─────────────────────────────────────────────────────────────

function throwMissingKey(column, key, extra) {
  const err = new Error(
    `[M02·DB_十神] ${column} key ${JSON.stringify(key)} not found in ` +
      "AUTHORITATIVE_DATASET_COMPLETE. No fuzzy match or substitution is performed."
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = "DB_十神";
  err.column = column;
  err.key = key;
  if (extra) Object.assign(err, extra);
  throw err;
}

/**
 * Exact-match composite-key lookup on the 100-row 十神 matrix.
 * `compositeKey` must be `<日主天干> + "_" + <对方天干>` (audited glyphs).
 *
 * @param {string} compositeKey e.g. "甲_丙"
 * @returns {string} the audited 十神 token
 */
export function shiShenLookup(compositeKey) {
  if (typeof compositeKey !== "string") {
    throw new TypeError("[M02·DB_十神] compositeKey must be a string");
  }
  const row = SHI_SHEN_ROWS.find((r) => r.compositeKey === compositeKey);
  if (!row) throwMissingKey("composite", compositeKey);
  return row.shiShen;
}

/**
 * Convenience: composite-key lookup by (dayStem, targetStem) pair.
 * Composes the audited key internally as `dayStem + "_" + targetStem`.
 *
 * @param {string} dayStem   日主天干 glyph
 * @param {string} targetStem 对方天干 glyph
 * @returns {string} the audited 十神 token
 */
export function shiShenLookupBy(dayStem, targetStem) {
  if (typeof dayStem !== "string" || typeof targetStem !== "string") {
    throw new TypeError(
      "[M02·DB_十神] dayStem and targetStem must both be strings"
    );
  }
  return shiShenLookup(dayStem + KEY_SEP + targetStem);
}

/**
 * Return the FULL frozen row for a composite key (composite key, riZhu,
 * duiFang, shiShen) — useful for auditors and integration tests.
 */
export function shiShenRowLookup(compositeKey) {
  if (typeof compositeKey !== "string") {
    throw new TypeError("[M02·DB_十神] compositeKey must be a string");
  }
  const row = SHI_SHEN_ROWS.find((r) => r.compositeKey === compositeKey);
  if (!row) throwMissingKey("compositeRow", compositeKey);
  return row;
}

/**
 * Five-element auxiliary lookup — returns the audited row for the given
 * subject element (column A of A2:E6). Callers may then read
 * `.woShengShiShang / .woKeCai / .shengWoYin / .keWoGuanSha` to get the
 * corresponding relationship element.
 *
 * NOTE — API semantics are anchored to the Unicode-authoritative headers.
 * The transcribed labels ("同类"/"同生"/"生同"/"克同") were rejected per
 * MIGRATION_RULES.md → Encoding Warning. See HEADER DISCREPANCY RESOLUTION
 * in this file's docstring.
 *
 * @param {string} wuXing subject five-element glyph
 * @returns {{wuXing:string, woShengShiShang:string, woKeCai:string, shengWoYin:string, keWoGuanSha:string}}
 */
export function fiveElementAuxLookup(wuXing) {
  if (typeof wuXing !== "string") {
    throw new TypeError("[M02·DB_十神·A2:E6] wuXing must be a string");
  }
  const row = FIVE_ELEMENT_AUX_ROWS.find((r) => r.wuXing === wuXing);
  if (!row) throwMissingKey("fiveElementAux", wuXing);
  return row;
}
